import React, { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react'
import { SecureWrapper } from '../layout/SecureWrapper'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  CompleteLMSTrainingStep,
  FetchLMSTrainingState,
  FetchDriver,
  UploadLMSCertificate,
} from 'src/constants/actionTypes'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { LMSTrainingCollectDataStep } from 'src/interfaces/collectData'
import { handleApiError } from 'src/utils/errorHandlers'
import { LoadingPage } from 'src/pages/LoadingPage'
import tw from 'twin.macro'
import { CircleAlertMajor, NoteMajor } from '@shopify/polaris-icons'
import { Tooltip, Typography } from '@mui/material'
import { Button } from '../shared/Button'
import { GSIcon } from '../shared/GSIcon'
import { InfoContainer } from '../shared/InfoContainer'
import { useQuery } from 'src/utils/hooks/useQuery'
import { useLocation, useNavigate } from 'react-router-dom'
import Lottie from 'lottie-react'
import iosActivityIndicator from 'src/assets/animations/IOSActivityIndicator.json'
import { usePolling } from 'src/utils/hooks/usePolling'
import { BasicRadioGroup } from '../shared/form/BasicRadioGroup'
import { FileUploadInput } from '../shared/FileUploadInput'
import { UploadedFile } from '../shared/UploadedFile'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { StepLabels } from '../client/step-labels/StepLabels'
import { MAX_FILE_SIZE_BYTES } from 'src/constants/fileUpload'
import { replaceTrainingWording } from 'src/utils/certificationWording'
import {
  LMS_TRAINING_COMPLETED_EVENT,
  PendingReturnStepSource,
  savePendingReturnStep,
} from 'src/utils/pendingReturnStep'

const TRAINING_INFO_UPDATE_POLLING_INTERVAL = 1000 * 5
const CERTIFICATE_GENERATION_TIMEOUT_MS = 1000 * 60

const UPLOAD_ACCEPT_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
const UPLOAD_FILE_SIZE_LIMIT = MAX_FILE_SIZE_BYTES

enum LMSOptionType {
  Partner = 'partner',
  Manual = 'manual',
}

const decodeHtmlEntities = (html: string): string => {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = html

  return textarea.value
}

const LMSTrainingComponent = () => {
  const dispatch = useDispatch()
  const { getQuery } = useQuery()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const currentStep = useSelector(getCurrentCollectDataStep) as LMSTrainingCollectDataStep

  const [isLoaded, setIsLoaded] = useState(false)
  const [certificateUrl, setCertificateUrl] = useState('')
  const [signedUrl, setSignedUrl] = useState('')
  const [enableManualUpload, setEnableManualUpload] = useState(false)
  const [manualUploadRequirements, setManualUploadRequirements] = useState('')
  const [selectedOption, setSelectedOption] = useState<LMSOptionType | undefined>(undefined)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isGenerationTimedOut, setIsGenerationTimedOut] = useState(false)
  const [hasLoadError, setHasLoadError] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)

  const generationTimeoutRef = useRef<number | null>(null)

  const [isTrainingCompleted] = useState(() => getQuery('event') === LMS_TRAINING_COMPLETED_EVENT)

  useEffect(() => {
    const params = new URLSearchParams(search)

    if (!params.has('event') && !params.has('stepId')) {
      return
    }

    params.delete('event')
    params.delete('stepId')

    navigate({ pathname, search: params.toString() }, { replace: true })
  }, [navigate, pathname, search])

  const canContinueToNextStep = Boolean(certificateUrl) || isGenerationTimedOut
  const trainingName = replaceTrainingWording(currentStep?.metadata?.lmsTrainingName ?? '')
  const certificateTitle = trainingName || 'Your certificate'

  const clearGenerationTimeout = useCallback(() => {
    if (generationTimeoutRef.current === null) {
      return
    }

    window.clearTimeout(generationTimeoutRef.current)
    generationTimeoutRef.current = null
  }, [])

  const lmsOptions = [
    { label: 'I want to obtain the certificate through a partner', value: LMSOptionType.Partner },
    {
      label: 'I would like to submit my own certificate for approval',
      value: LMSOptionType.Manual,
    },
  ]

  const fetchTrainingInfo = useCallback(async () => {
    try {
      const {
        certificateUrl: fetchedCertificateUrl,
        signedUrl: fetchedSignedUrl,
        enableManualUpload: fetchedEnableManualUpload,
        manualUploadRequirements: fetchedManualUploadRequirements,
      }: {
        certificateUrl?: string
        signedUrl: string
        enableManualUpload?: boolean
        manualUploadRequirements?: string
      } = await createAsyncAction(
        dispatch,
        FetchLMSTrainingState.request({
          lmsTrainingId: currentStep.metadata.lmsTrainingId,
          stepId: currentStep.id,
        }),
      )

      if (fetchedCertificateUrl) {
        clearGenerationTimeout()
      }

      setCertificateUrl(fetchedCertificateUrl || '')
      setSignedUrl(fetchedSignedUrl)
      setEnableManualUpload(fetchedEnableManualUpload || false)
      setManualUploadRequirements(fetchedManualUploadRequirements || '')
      setHasLoadError(false)
    } catch {
      // Shown inline instead of a toast per polling attempt: the initial view is replaced by a
      // retry state, the certificate view keeps polling. The saga already reports to Sentry.
      setHasLoadError(true)
    }

    setIsLoaded(true)
  }, [clearGenerationTimeout, dispatch, currentStep.metadata.lmsTrainingId, currentStep.id])

  const handleRetryLoad = useCallback(async () => {
    setIsRetrying(true)
    await fetchTrainingInfo()
    setIsRetrying(false)
  }, [fetchTrainingInfo])

  useEffect(() => {
    fetchTrainingInfo()
  }, [fetchTrainingInfo])

  const { startPolling: trainingStartPolling, stopPolling: trainingStopPolling } = usePolling(
    () => {
      fetchTrainingInfo()

      dispatch(FetchDriver.request())
    },
    TRAINING_INFO_UPDATE_POLLING_INTERVAL,
  )

  useEffect(() => {
    if (!isTrainingCompleted || certificateUrl) {
      trainingStopPolling()

      return
    }

    trainingStartPolling()

    return () => {
      trainingStopPolling()
    }
  }, [certificateUrl, isTrainingCompleted, trainingStartPolling, trainingStopPolling])

  useEffect(() => {
    clearGenerationTimeout()

    if (!isTrainingCompleted || certificateUrl) {
      return
    }

    generationTimeoutRef.current = window.setTimeout(() => {
      generationTimeoutRef.current = null
      setIsGenerationTimedOut(true)
    }, CERTIFICATE_GENERATION_TIMEOUT_MS)

    return () => {
      clearGenerationTimeout()
    }
  }, [certificateUrl, clearGenerationTimeout, isTrainingCompleted])

  // The course is a full-page navigation away, so the return lands in a fresh page load. In
  // recollection the selected step only lives in Redux: carry the step id in the return URL, and
  // save it as a fallback in case the LMS drops it (see ClientDocsPage).
  const handleStartTraining = useCallback(() => {
    const redirectParams = new URLSearchParams({
      event: LMS_TRAINING_COMPLETED_EVENT,
      stepId: currentStep.id,
    })
    const redirectUrl = `${window.location.origin}/client/documents?${redirectParams.toString()}`

    savePendingReturnStep(PendingReturnStepSource.LMSTraining, currentStep.id)

    window.open(`${signedUrl}&redirect_back=${encodeURIComponent(redirectUrl)}`, '_self')
  }, [signedUrl, currentStep.id])

  const handleCompleteStep = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        CompleteLMSTrainingStep.request({
          stepId: currentStep.id,
          skipCertificateValidation: !certificateUrl,
        }),
      )

      await createAsyncAction(dispatch, FetchDriver.request())
    } catch (error) {
      handleApiError(error)
    }
  }, [dispatch, currentStep.id, certificateUrl])

  const handleUploadCertificate = useCallback(async () => {
    if (!uploadedFile) {
      return
    }

    setIsUploading(true)
    setUploadError(null)

    try {
      await createAsyncAction(
        dispatch,
        UploadLMSCertificate.request({
          stepId: currentStep.id,
          file: uploadedFile,
        }),
      )
    } catch (err: any) {
      setUploadError(err)
    } finally {
      setIsUploading(false)
    }
  }, [dispatch, currentStep.id, uploadedFile])

  if (!isLoaded) {
    return <LoadingPage flow="client" />
  }

  const renderHeader = () => (
    <Fragment>
      <div
        css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
      >
        <NoteMajor width={20} />
      </div>
      <StepLabels />
      <Typography css={tw`mb-2 mt-2`} variant="h2">
        Certification Step
      </Typography>
    </Fragment>
  )

  if (!signedUrl) {
    return (
      <SecureWrapper
        footer={
          <Button
            className={tw`w-full`}
            onClick={handleRetryLoad}
            loading={isRetrying}
            data-testid="contractors-onboarding-lms-training-retry-btn"
          >
            Try again
          </Button>
        }
      >
        {renderHeader()}
        <InfoContainer color="error" className={tw`mt-2`}>
          We couldn't load your certification right now. Please try again in a few minutes. If it
          keeps failing, contact us through Get Help.
        </InfoContainer>
      </SecureWrapper>
    )
  }

  const renderInitialView = () => {
    if (!enableManualUpload) {
      return (
        <SecureWrapper
          footer={
            <Button
              className={tw`w-full`}
              onClick={handleStartTraining}
              data-testid="contractors-onboarding-lms-training-start-btn"
            >
              Start Certification Process
            </Button>
          }
        >
          {renderHeader()}
          <Typography css={tw`text-shades-400`}>
            Let's start certification process. After clicking the button below you will be
            redirected to complete "
            <span css={tw`font-bold text-shades-900 text-[18px]`}>{trainingName}</span>" course on
            our learning platform, where you will be guided through all required steps to complete
            it.
          </Typography>
          <Typography css={tw`text-shades-400 mb-4`}>
            Once you complete your certification, it will be automatically added to your profile,
            and you'll also be able to download it.
          </Typography>
        </SecureWrapper>
      )
    }

    return (
      <SecureWrapper
        footer={
          <Fragment>
            <FormErrorMessage css={tw`mb-4`} error={uploadError} />
            {selectedOption === LMSOptionType.Partner && (
              <Button
                className={tw`w-full`}
                onClick={handleStartTraining}
                data-testid="contractors-onboarding-lms-training-start-btn"
              >
                Start Certification Process
              </Button>
            )}
            {selectedOption === LMSOptionType.Manual && (
              <Button
                className={tw`w-full`}
                onClick={handleUploadCertificate}
                disabled={!uploadedFile}
                loading={isUploading}
                data-testid="contractors-onboarding-lms-training-upload-submit-btn"
              >
                Submit and Continue
              </Button>
            )}
          </Fragment>
        }
      >
        {renderHeader()}
        <Typography css={tw`text-shades-400 mb-4`}>
          Start your certification process for the "
          <span css={tw`font-bold text-shades-900 text-[18px]`}>{trainingName}</span>" course with
          our verified partner. Once completed, your certificate will be available for download and
          automatically added to your profile. If you already have a valid certificate, you can
          upload it for verification instead.
        </Typography>
        <div
          css={[
            tw`rounded-2xl p-4 bg-shades-0 mb-4 mt-2`,
            tw`[& .MuiFormControlLabel-root]:(py-3 pr-3 pl-0 border-shades-200 bg-white) [& .MuiFormControlLabel-root:has(.Mui-checked)]:border-primary-500 [& .MuiFormControlLabel-root .MuiTypography-root]:text-shades-400 [& .MuiRadio-root]:text-shades-200 [& .MuiRadio-root.Mui-checked]:text-primary-500`,
          ]}
        >
          <BasicRadioGroup
            label="Please select your preferred option to continue"
            value={selectedOption}
            options={lmsOptions}
            onChange={(_, value) => {
              setSelectedOption(value as LMSOptionType)
              setUploadedFile(null)
              setUploadError(null)
            }}
            dataTestId="contractors-onboarding-lms-training-option-input"
          />
          {selectedOption === LMSOptionType.Manual && (
            <Fragment>
              {manualUploadRequirements && (
                <div css={tw`p-4 rounded-2xl flex flex-row items-start bg-shades-100 mb-4 mt-4`}>
                  <div css={tw`w-5 mr-4 h-5`}>
                    <CircleAlertMajor width={20} height={20} css={tw`fill-shades-500`} />
                  </div>
                  <Typography
                    css={tw`text-shades-500 [& p]:(m-0) [& ul]:(list-disc pl-5 m-0) [& li]:(m-0) [& strong]:font-semibold [& em]:italic`}
                    dangerouslySetInnerHTML={{
                      __html: decodeHtmlEntities(manualUploadRequirements),
                    }}
                  />
                </div>
              )}
              <Typography css={tw`mb-1.5 text-shades-400`} variant="h6">
                Upload certificate
              </Typography>
              {uploadedFile ? (
                <UploadedFile
                  file={uploadedFile}
                  onRemove={() => {
                    setUploadedFile(null)
                    setUploadError(null)
                  }}
                  removeButtonTestId="contractors-onboarding-lms-training-upload-remove-btn"
                />
              ) : (
                <FileUploadInput
                  acceptTypes={UPLOAD_ACCEPT_TYPES}
                  fileSizeLimit={UPLOAD_FILE_SIZE_LIMIT}
                  onSelect={(file) => {
                    setUploadedFile(file)
                    setUploadError(null)
                  }}
                  inputDataTestId="contractors-onboarding-lms-training-upload-input"
                />
              )}
              <Typography css={tw`mt-2 text-shades-400 text-center`}>
                After you upload a file, you'll be able to upload another one
              </Typography>
            </Fragment>
          )}
        </div>
      </SecureWrapper>
    )
  }

  if (!certificateUrl && !isTrainingCompleted) {
    return renderInitialView()
  }

  return (
    <SecureWrapper
      footer={
        <Button
          className={tw`w-full`}
          disabled={!canContinueToNextStep}
          onClick={handleCompleteStep}
          data-testid={
            canContinueToNextStep
              ? 'contractors-onboarding-lms-training-continue-btn'
              : 'contractors-onboarding-lms-training-continue-pending-btn'
          }
        >
          Continue to next step
        </Button>
      }
    >
      <div
        css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
      >
        <NoteMajor width={20} />
      </div>
      <StepLabels />
      <Typography css={tw`mb-2 mt-2`} variant="h2">
        Get Your Certificate
      </Typography>
      <Typography css={tw`text-shades-400`}>
        Your certificate will be available to download here once it's ready, or later from the
        Contractor Portal.
      </Typography>
      <div css={tw`flex mt-4 items-center flex-row p-4 rounded-2xl bg-shades-0`}>
        <div css={tw`mr-3 shrink-0`}>
          <GSIcon iconName="gs_notepad" width={20} height={20} />
        </div>
        <div css={tw`flex-1 min-w-0`}>
          <Typography css={tw`text-shades-1000`} variant="h4">
            Your certificate
          </Typography>
          <Tooltip
            title={certificateUrl ? certificateTitle : ''}
            disableHoverListener={!certificateUrl}
          >
            <Typography css={tw`block w-full mt-0.5 text-shades-400 truncate`} variant="caption">
              {certificateUrl ? certificateTitle : 'Generating...'}
            </Typography>
          </Tooltip>
        </div>
        {certificateUrl ? (
          <a
            css={tw`no-underline shrink-0 ml-3`}
            href={certificateUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="contractors-onboarding-lms-training-certificate-link"
          >
            <Typography css={tw`text-primary-500`} fontWeight={600}>
              Download
            </Typography>
          </a>
        ) : (
          <div css={tw`w-6 h-6 shrink-0 ml-3`}>
            <Lottie animationData={iosActivityIndicator} loop={true} />
          </div>
        )}
      </div>
      {!certificateUrl && !isGenerationTimedOut && !hasLoadError && (
        <InfoContainer color="error" className={tw`mt-4`}>
          Generating your certificate. This takes about a minute. Please don't close this page.
        </InfoContainer>
      )}
      {!certificateUrl && !isGenerationTimedOut && hasLoadError && (
        <InfoContainer color="error" className={tw`mt-4`}>
          We couldn't check on your certificate right now. We'll keep trying, so please don't close
          this page.
        </InfoContainer>
      )}
      {!certificateUrl && isGenerationTimedOut && (
        <InfoContainer className={tw`mt-4`}>
          Your certificate is taking a little longer than expected. You can continue onboarding and
          download it later from the Contractor Portal.
        </InfoContainer>
      )}
    </SecureWrapper>
  )
}

export const LMSTraining = memo(LMSTrainingComponent)
